import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AppearanceFields,
  type AppearanceValue,
} from "@/components/shell/BrandAppearanceFields";
import {
  AdminAlert,
  AdminPageHeader,
  AdminPill,
  AdminSectionCard,
} from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  useOptionalAccountWorkspace,
  type WorkspaceAccount,
} from "@/lib/account-workspace";

type Account = WorkspaceAccount;
type Membership = { user_id: number; role: "owner" | "admin" | "member" };

const accountSchema = z.object({
  name: z.string().trim().min(1, "请输入账户名称").max(100, "账户名称不能超过 100 个字符"),
});

type AccountFormValues = z.infer<typeof accountSchema>;

export default function AccountDetailPage() {
  const { accountId: id } = useParams();
  const { user } = useAuth();
  const workspace = useOptionalAccountWorkspace();
  const [account, setAccount] = useState<Account | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appearance, setAppearance] = useState<AppearanceValue>({
    iconKey: null,
    color: null,
  });
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
  } = useForm<AccountFormValues>({
    resolver: zodResolver(accountSchema),
    defaultValues: { name: "" },
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (!id) return;
    api
      .fetchForAccount<{ account: Account }>(id, `/accounts/${id}`)
      .then(({ account: fetchedAccount }) => {
        setAccount(fetchedAccount);
        reset({ name: fetchedAccount.name });
        setAppearance({
          iconKey: fetchedAccount.icon_key ?? null,
          color: fetchedAccount.color ?? null,
        });
        return api.fetchForAccount<{ memberships: Membership[] }>(id, `/accounts/${id}/memberships`);
      })
      .then(({ memberships }) => {
        const membership = memberships.find((item) => item.user_id === user?.id);
        setCanManage(membership?.role === "owner" || membership?.role === "admin");
      })
      .catch((caughtError) =>
        setError(caughtError instanceof ApiError ? caughtError.message : "无法加载账户"),
      );
  }, [id, reset, user?.id]);

  async function onSubmit({ name }: AccountFormValues) {
    if (!id) return;
    clearErrors("root");
    try {
      const { account: updatedAccount } = await api.fetchForAccount<{ account: Account }>(id, `/accounts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          icon_key: appearance.iconKey,
          color: appearance.color,
        }),
      });
      setAccount(updatedAccount);
      reset({ name: updatedAccount.name });
      setAppearance({
        iconKey: updatedAccount.icon_key ?? null,
        color: updatedAccount.color ?? null,
      });
      await workspace?.refreshAccounts();
      toast.success("账户设置已保存");
    } catch (caughtError) {
      setFormError("root", {
        message: caughtError instanceof ApiError ? caughtError.message : "保存账户失败，请稍后重试",
      });
    }
  }

  if (error && !account) {
    return <AdminAlert>{error}</AdminAlert>;
  }
  if (!account) {
    return <p className="text-sm text-muted-foreground">正在加载账户…</p>;
  }

  const verified = Boolean(account.enterprise_verified_at);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="账户设置"
        description={
          <div className="flex flex-wrap items-center gap-2">
            <span>账户 #{account.id}</span>
            <AdminPill tone={verified ? "success" : "warning"}>
              {verified ? "已企业认证" : "未企业认证"}
            </AdminPill>
          </div>
        }
      />

      {!canManage ? (
        <AdminAlert tone="info">你拥有只读访问权限，无法修改账户设置。</AdminAlert>
      ) : (
        <AdminSectionCard title="基本信息" description="修改账户对外显示名称与侧栏图标。">
          <form className="max-w-lg space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="space-y-2">
              <Label htmlFor="account-name">账户名称</Label>
              <Input id="account-name" {...register("name")} aria-invalid={Boolean(errors.name)} />
              {errors.name && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.name.message}
                </p>
              )}
            </div>
            <AppearanceFields
              name={nameValue}
              value={appearance}
              onChange={setAppearance}
              emptyNameLabel="账户"
              description="默认用账户名首字；也可从 Lucide 精选图标中选择（可搜索）。"
              modal={false}
              idPrefix="account-appearance"
            />
            {errors.root && <AdminAlert>{errors.root.message}</AdminAlert>}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "保存中…" : "保存"}
            </Button>
          </form>
        </AdminSectionCard>
      )}
    </div>
  );
}
